import React from 'react';

import { createGlobalStyle } from 'styled-components';

import VendorRedirect from 'pages/vendor-redirect/vendor-redirect';
import { useLocationContext } from 'state/location';

import AccordionWidget from '../accordionWidget';
import AnchorLinksWidget from '../anchorLinksWidget';
import CallToActionWidget from '../callToActionWidget';
import DonationWidget from '../donationWidget';
import DoubleImageWidget from '../doubleImageWidget';
import DownloadFileWidget from '../downloadFileWidget';
import HeaderWidget from '../headerWidget';
import ImageWidget from '../imageWidget';
import NutritionExplorerWidget from '../nutrition-explorer-widget';
import NutritionInfoWidget from '../nutritionInfoWidget';
import PreviewWidget from '../previewWidget';
import QuoteWidget from '../quoteWidget';
import RewardsCarouselWidget from '../rewards-carousel-widget';
import theme from '../theme';
import VideoWidget from '../videoWidget';
import WebViewWidget from '../webViewWidget';
import YouTubeWidget from '../youtubeWidget';

import { AnchorWidget, BlockWidgetContainer, Row, UnknownWidget } from './styles';

// Remove everything that is not a letter / digit / dash
const sanitizeAlphanumeric = (v = '') => v.replace(/[^A-Za-z0-9-]/g, '');

export const RenderCurrentStaticPage = ({ currentPage }) => {
  const { location } = useLocationContext();
  const { search } = location;
  const queryParams = Object.fromEntries(new URLSearchParams(search)?.entries());

  const RenderGlobalCss = createGlobalStyle`
    .static-page {
      color: ${Styles.color.black};
      background-color: ${theme.backgroundColor};
      ${currentPage && currentPage.pageCSS && currentPage.pageCSS.code}
  }`;

  // If the static page has a vendor ID, it's a 3rd party game static page
  const { vendorId, vendorRedirectPath } = currentPage;
  // Some 3rd party game did not have a vendorRedirectPath
  if (vendorId) {
    return <VendorRedirect vendor={vendorId} vendorPath={vendorRedirectPath?.locale} />;
  }

  let htmlCode = currentPage?.pageHtml?.code || '';

  Object.keys(queryParams).forEach(param => {
    htmlCode = htmlCode.replace(`{{{${param}}}}`, sanitizeAlphanumeric(queryParams[param]));
  });

  // Remove any unused placeholders
  htmlCode = htmlCode.replace(/{{{[aA-zZ]+}}}/g, '');

  return (
    <>
      <RenderGlobalCss />

      {htmlCode && <div dangerouslySetInnerHTML={{ __html: htmlCode }} />}
      {currentPage.widgets &&
        currentPage.widgets.map((widget, index) => {
          const type = widget._type;
          const key = `widget${index}`;

          switch (type) {
            case 'webViewWidget':
              return (
                <Row key={key}>
                  <WebViewWidget {...widget} />
                </Row>
              );
            case 'rewardsCarouselWidget':
              return (
                <Row key={key}>
                  <RewardsCarouselWidget {...widget} />
                </Row>
              );
            case 'headerWidget':
              return (
                <Row key={key}>
                  <HeaderWidget {...widget} />
                </Row>
              );
            case 'anchorWidget':
              return (
                <Row key={key} {...widget}>
                  <AnchorWidget
                    className="anchorWidget"
                    id={widget.anchorName?.locale.replace(/\s/g, '')}
                  />
                </Row>
              );
            case 'anchorLinksWidget':
              return (
                <Row key={key}>
                  <AnchorLinksWidget widgets={currentPage.widgets} />
                </Row>
              );
            case 'downloadFileWidget':
              return (
                <Row key={key}>
                  <DownloadFileWidget widgets={currentPage.widgets} />
                </Row>
              );
            case 'localeBlockTextWidget':
              return (
                <Row key={key} {...widget}>
                  <BlockWidgetContainer content={widget?.localeBlockTextContent?.localeRaw} />
                </Row>
              );
            case 'doubleImageWidget':
              return (
                <Row key={key}>
                  <DoubleImageWidget {...widget} />
                </Row>
              );
            case 'imageWidget':
              return (
                <Row key={key}>
                  <ImageWidget {...widget} currentPage={currentPage.path.current} />
                </Row>
              );
            case 'callToActionWidget':
              return (
                <Row key={key}>
                  <CallToActionWidget {...widget} />
                </Row>
              );
            case 'accordionWidget':
              return (
                <Row key={key}>
                  <AccordionWidget {...widget} />
                </Row>
              );
            case 'previewWidget':
              return (
                <Row key={key}>
                  <PreviewWidget {...widget} />
                </Row>
              );
            case 'nutritionExplorerWidget':
              return (
                <Row key={key}>
                  <NutritionExplorerWidget {...widget} />
                </Row>
              );
            case 'videoWidget':
              return (
                <Row key={key}>
                  <VideoWidget {...widget} />
                </Row>
              );
            case 'quoteWidget':
              return (
                <Row key={key}>
                  <QuoteWidget {...widget} />
                </Row>
              );
            case 'multiWidget':
              if (widget.widget === 'donationWidget') {
                return (
                  <Row key={key}>
                    <DonationWidget id="96818434-aee1-462f-a4f7-428c583b8396" {...widget} />
                  </Row>
                );
              }

              // TODO: Remove multi widget in favor of a real donation widget
              // https://github.com/rbilabs/ctg-whitelabel-app/pull/3608
              if (widget.widget === 'donationWidgetNuggets') {
                return (
                  <Row key={key}>
                    <DonationWidget id="24129188-142d-40cb-aab3-5d19b33d223b" {...widget} />
                  </Row>
                );
              }

              return (
                <Row key={key}>
                  <UnknownWidget> Placeholder: {type} </UnknownWidget>
                </Row>
              );
            case 'nutritionInfoWidget':
              return (
                <Row key={key}>
                  <NutritionInfoWidget {...widget} />
                </Row>
              );
            case 'youtubeWidget':
              return (
                <Row key={key}>
                  <YouTubeWidget {...widget} />
                </Row>
              );
            default:
              return (
                <Row key={key}>
                  <UnknownWidget> Placeholder: {type} </UnknownWidget>
                </Row>
              );
          }
        })}
    </>
  );
};
